import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentsService, IOcbCULocalAgentExecutionTrigger, IOcbLocalAgent, IOcbLocalAgentExecutionTrigger, LocalAgentsService, OcbDays } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { Observable, forkJoin, from as fromPromise } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-local-agents-execution-triggers',
    templateUrl: './local-agents-execution-triggers.component.html',
    styleUrls: ['./local-agents-execution-triggers.component.scss'],
})
export class LocalAgentsExecutionTriggersComponent implements AfterViewInit {

    @ViewChild('executionTriggers')
    public etGrid: DxDataGridComponent;
    @ViewChild('executionTriggerValidation')
    public etValidationGroup: DxValidationGroupComponent;
    @ViewChild('agentPicker')
    public agentPicker: DxSelectBoxComponent;

    // Main Data-Grid
    public dataSource: IOcbLocalAgentExecutionTrigger[] = null;

    // List of local agents
    public localAgents: IOcbLocalAgent[] = null;
    public requestedAgentId?: string;
    public selectedLocalAgent: IOcbLocalAgent = null;

    // Popup New & Edit
    public isPopupVisible: boolean = false;
    public selectedExecutionTrigger: IOcbLocalAgentExecutionTrigger = {
        localAgentId: '',
    };
    public isNew: boolean = null;

    public isDropDownBoxOpened: boolean = false;

    public isLoadPanelVisible: boolean = true;
    public currentEnvironmentName: string = '';

    private currentEnvironmentId: string = '';
    private daysLabels: any;

    constructor(
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private environmentsService: EnvironmentsService,
        private localAgentsService: LocalAgentsService,
    ) {
        this.displayDay = this.displayDay.bind(this);
    }

    public ngAfterViewInit() {
        this.route.queryParams.subscribe(queryParams => {
            if (queryParams['agent']) this.requestedAgentId = queryParams['agent'];
        });
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.environmentsService.getEnvironment(this.currentEnvironmentId).subscribe(env => this.currentEnvironmentName = env.name);
            this.translateService.get('days').subscribe(labels => {
                this.daysLabels = labels;
                this.loadLocalAgents();
            });
        });
    }

    public displayNewPopup(): void {
        if (this.selectedLocalAgent) {
            this.selectedExecutionTrigger.day = null;
            this.selectedExecutionTrigger.hour = null;
            this.isNew = true;
            this.isPopupVisible = true;
        }
        else 
            this.translateService.get('localAgentsExecutionTriggers.msgLocalAgentRequired').subscribe(message => notify(message, 'error', 3000));
    }

    public createExecutionTrigger(): void {
        if (this.etValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const executionTrigger: IOcbCULocalAgentExecutionTrigger = {
                day: this.selectedExecutionTrigger.day,
                hour: this.selectedExecutionTrigger.hour,
            };
            this.localAgentsService.addExecutionTrigger(this.selectedLocalAgent.id, executionTrigger)
                .pipe(
                    mergeMap(_ => this.translateService.get('localAgentsExecutionTriggers.msgCreationComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public deleteExecutionTrigger(id): void {
        forkJoin([
            this.translateService.get('localAgentsExecutionTriggers.msgDeleteConfirm'),
            this.translateService.get('localAgentsExecutionTriggers.msgDeleteConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.localAgentsService.deleteExecutionTrigger(this.selectedLocalAgent.id, id)),
                // Reload the email template
                mergeMap(_ => this.translateService.get('localAgentsExecutionTriggers.msgDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, error: (err) => this.handleError(err)
            });
    }

    public filterChanged(e): void {
        if (e) {
            this.selectedLocalAgent = e;
            this.isDropDownBoxOpened = false;
            this.loadDataSource();
        }
        else this.dataSource = [];
    }

    public displayDay(rowData) { return this.daysLabels[OcbDays[rowData.day]]; }

    private loadLocalAgents(): void {
        this.localAgentsService.getLocalAgents(this.currentEnvironmentId, false)
            .pipe(
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (res) => {
                    this.localAgents = res;
                    if (this.requestedAgentId && this.agentPicker) this.agentPicker.value = this.requestedAgentId;
                },
                error: (err) => this.handleError(err)
            });
    }

    private loadDataSource(): void {
        if (this.selectedLocalAgent) {
            this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.localAgentsService.getExecutionTriggers(this.selectedLocalAgent.id)),
                finalize(() => this.etGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.dataSource = res,
                    error: (err) => this.handleError(err)
                });
        } else {
            this.translateService.get('_pickers.msgLocalAgentRequired')
                .subscribe(message => notify(message, 'error', 3000));
        }
    }

    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.etGrid.instance.beginCustomLoading(`${message}...`))
        );
    }

    private handleError(error: any): void {
        let message: string = '';
        if (error instanceof HttpErrorResponse) {
            if (error.error) {
                if (error.error.hasOwnProperty('message')) message = error.error.message;
                else if (error.error.hasOwnProperty('name')) message = error.error.name;
                else message = error.error;
            }
            else
                message = error.message;
        }
        else message = error;

        notify(message, 'error', 3000);
    }
}
